import { useParams, useNavigate, useLocation } from "react-router";
import { ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentSection } from "@/features/dash/registrations/components/payment-section";
import { ChildDetailSheet } from "@/features/dash/registrations/components/child-detail-sheet";
import { useState } from "react";
import type { AdultBase, ChildBase } from "nautika-types";

// Fonction utilitaire pour calculer l'âge
function calculateAge(birthDate: string | Date | number | Record<string, never>): number {
  const birth = new Date(birthDate as string | Date | number);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

// Fonction pour formater la date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Types for navigation filtering
type RegistrationNavItem = {
  id: string;
  archived: string | null;
  subtotalAmount: number;
  paymentDetails: { card: number; ancv: number; cash: number; check: number };
};

export function RegistrationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const activeTab = params.get("tab") || "all";

  // État pour la Sheet de détail enfant
  const [selectedChild, setSelectedChild] = useState<ChildBase | null>(null);
  const [isChildDetailOpen, setIsChildDetailOpen] = useState(false);
  // Mois initial à afficher dans la feuille de détail enfant
  const [initialMonth, setInitialMonth] = useState<Date | undefined>(undefined);

  // Fonction pour ouvrir la Sheet de détail enfant
  const handleViewChildDetails = (child: ChildBase) => {
    // Récupérer les périodes réservées depuis l'API pour cet enfant
    const childReservations = registration.reservations?.find((res) => res.childId === child.id);

    const bookedReservations = childReservations ? childReservations.reservations || [] : [];
    const bookedPeriodIds = bookedReservations.map((res) => (res.period ? res.period.id : ""));
    // Calcul du premier mois des réservations
    if (bookedReservations.length > 0) {
      const dates = bookedReservations.map((res) => new Date(res.period.startTime as string | number | Date));
      const earliest = dates.reduce((a: Date, b: Date) => (a < b ? a : b));
      setInitialMonth(new Date(earliest.getFullYear(), earliest.getMonth(), 1));
    }

    const childWithPeriods = {
      ...child,
      bookedPeriods: bookedPeriodIds,
    };

    setSelectedChild(childWithPeriods as ChildBase & { bookedPeriods?: string[] });
    setIsChildDetailOpen(true);
  }; // Récupération des données d'inscription
  const { data: registrationResponse, isLoading } = api.useQuery("get", "/registrations/{id}", {
    params: {
      path: { id: id || "" },
    },
  });

  // Récupération de toutes les inscriptions pour la navigation
  const { data: allRegistrationsResponse } = api.useQuery("get", "/registrations/", {});

  // Vérification de la réponse d'inscription
  if (!registrationResponse) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        {/* Conteneur séparé pour les informations de navigation - Skeleton */}
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Skeleton pagination */}
              <div className="flex items-center space-x-2">
                <Skeleton className="h-6 w-6" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-6" />
              </div>
              <Skeleton className="h-5 w-32" />
            </div>

            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ml-2 h-6 w-12" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl space-y-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </main>
      </div>
    );
  }
  const registration = registrationResponse;

  // Indicateurs d'inscription à compléter
  const priceMissing = (registration.subtotalAmount ?? 0) === 0;
  const hasNoAdults = (registration.adults?.length ?? 0) === 0;

  // Calcul du montant de la promotion
  const promotionDiscount =
    registration.promotions && registration.promotions.length > 0
      ? registration.promotions.reduce((total, promo) => {
          const promoObj = promo;
          if (promoObj.type === "percentage") {
            return total + ((registration?.subtotalAmount ?? 0) * promoObj.value) / 100;
          } else {
            // Type "fixed" - montant fixe en centimes, convertir en euros
            return total + promoObj.value / 100;
          }
        }, 0)
      : 0;

  // Calcul du montant total après promotion
  const totalAfterPromotion = (registration.subtotalAmount ?? 0) - promotionDiscount;

  // Calcul du montant restant à payer
  const remainingAmount =
    totalAfterPromotion -
    ((registration.paymentDetails?.card ?? 0) +
      (registration.paymentDetails?.ancv ?? 0) +
      (registration.paymentDetails?.cash ?? 0) +
      (registration.paymentDetails?.check ?? 0));

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        {/* Conteneur séparé pour les informations de navigation - Skeleton */}
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Skeleton pagination */}
              <div className="flex items-center space-x-2">
                <Skeleton className="h-6 w-6" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-6" />
              </div>
              <Skeleton className="h-5 w-32" />
            </div>

            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ml-2 h-6 w-12" />
              </div>
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl space-y-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </main>
      </div>
    );
  }

  if (!registrationResponse) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl">
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <p className="text-lg text-gray-500">Inscription non trouvée</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Calcul de la position dans la pagination
  const allRegistrations: RegistrationNavItem[] = allRegistrationsResponse ? allRegistrationsResponse : [];
  let registrationsList: RegistrationNavItem[] = [];
  if (Array.isArray(allRegistrations)) {
    switch (activeTab) {
      case "pending":
        registrationsList = allRegistrations.filter(
          (reg) =>
            reg.subtotalAmount - (reg.paymentDetails.card + reg.paymentDetails.ancv + reg.paymentDetails.cash + reg.paymentDetails.check) > 0 && !reg.archived
        );
        break;
      case "archived":
        registrationsList = allRegistrations.filter((reg) => reg.archived);
        break;
      case "all":
      default:
        registrationsList = allRegistrations.filter((reg) => !reg.archived);
        break;
    }
  }
  const currentIndex = registrationsList.findIndex((reg) => reg.id === registration.id);
  const totalRegistrations = registrationsList.length;

  // Navigation entre inscriptions
  const goToPreviousRegistration = () => {
    if (currentIndex > 0) {
      const prevRegistration = registrationsList[currentIndex - 1] as { id: string };
      navigate(`/dash/registrations/${prevRegistration.id}?tab=${activeTab}`);
    }
  };

  const goToNextRegistration = () => {
    if (currentIndex < totalRegistrations - 1) {
      const nextRegistration = registrationsList[currentIndex + 1] as { id: string };
      navigate(`/dash/registrations/${nextRegistration.id}?tab=${activeTab}`);
    }
  };

  // Titre avec les prénoms des enfants
  const childrenNames = registration.children.map((child: { firstName: string }) => child.firstName);

  return (
    <>
      {/* Conteneur séparé pour les informations de navigation */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Pagination avec boutons */}
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={goToPreviousRegistration} disabled={currentIndex <= 0} className="h-6 w-6 p-0">
                <ChevronUp className="h-3 w-3" />
              </Button>
              {currentIndex !== -1 && (
                <span className="text-sm text-gray-500">
                  {currentIndex + 1} sur {totalRegistrations}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={goToNextRegistration} disabled={currentIndex >= totalRegistrations - 1} className="h-6 w-6 p-0">
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            <span className="text-sm font-medium text-gray-900">{childrenNames.join(" · ")}</span>
          </div>

          <div className="flex items-center space-x-6 text-sm">
            {hasNoAdults && <Badge className="rounded-sm bg-orange-100 px-2 py-1 text-xs text-orange-700">Adultes à renseigner</Badge>}

            {priceMissing ? (
              <Badge className="rounded-sm bg-orange-100 px-2 py-1 text-xs text-orange-700">Prix à compléter</Badge>
            ) : (
              <span className="text-gray-600">
                Reste à payer (€) :
                <Badge className={`ml-2 rounded-sm px-2 py-1 text-xs ${remainingAmount > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                  {remainingAmount.toFixed(0)}€
                </Badge>
              </span>
            )}
            <span className="text-gray-600">Inscrit le {formatDate(String(registration.createdAt))}</span>
          </div>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto p-6 pb-48">
        <div className="mx-auto max-w-5xl space-y-6">
          {/* Bloc Adultes de confiance */}
          <Card className="gap-0 rounded-sm py-0">
            <CardHeader className="my-2 gap-0 text-center">
              <CardTitle className="text-md font-semibold">Adultes de confiance</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border-r p-3 text-center font-medium">Prénom</th>
                      <th className="border-r p-3 text-center font-medium">Nom</th>
                      <th className="border-r p-3 text-center font-medium">Mail</th>
                      <th className="p-3 text-center font-medium">Téléphone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registration.adults.map((adult: AdultBase, index: number) => {
                      return (
                        <tr key={adult.id} className={index < registration.adults.length - 1 ? "border-b" : ""}>
                          <td className="border-r p-3 text-center font-semibold">{adult.firstName}</td>
                          <td className="border-r p-3 text-center font-semibold">{adult.lastName}</td>
                          <td className="border-r p-3 text-center">{adult.email || "-"}</td>
                          <td className="p-3 text-center">{adult.phone || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Bloc Enfants */}
          <Card className="gap-0 rounded-sm py-0">
            <CardHeader className="my-2 gap-0 text-center">
              <CardTitle className="text-md font-semibold">Enfants</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border-r p-3 text-center font-medium">Prénom</th>
                      <th className="border-r p-3 text-center font-medium">Nom</th>
                      <th className="border-r p-3 text-center font-medium">Âge</th>
                      <th className="border-r p-3 text-center font-medium">Sexe</th>
                      <th className="border-r p-3 text-center font-medium">Groupe d'âge</th>
                      <th className="p-3 text-center font-medium">Dates, observations, droit à l’image etc...</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registration.children.map((child, index) => {
                      const childObj = child;
                      return (
                        <tr key={childObj.id} className={index < registration.children.length - 1 ? "border-b" : ""}>
                          <td className="border-r p-3 text-center font-semibold">{childObj.firstName}</td>
                          <td className="border-r p-3 text-center font-semibold">{childObj.lastName}</td>
                          <td className="border-r p-3 text-center">{calculateAge(childObj.birthDate)} ans</td>
                          <td className="border-r p-3 text-center">{childObj.gender === "M" ? "M" : childObj.gender === "F" ? "F" : "-"}</td>
                          <td className="border-r p-3 text-center">{childObj.groupName}</td>
                          <td className="p-3 text-center">
                            <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => handleViewChildDetails(childObj as ChildBase)}>
                              Voir plus
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Nouvelle section : Résumé financier */}
          <Card className="gap-0 rounded-sm py-0">
            <CardHeader className="my-2 gap-0 text-center">
              <CardTitle className="text-md font-semibold">Détail du prix</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="w-1/2 border-r p-3 text-center font-medium">Prix avant promotions</th>
                      <th className="w-1/2 p-3 text-center font-medium">Promotion</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="w-1/2 border-r p-3 text-center">{registration.subtotalAmount.toFixed(2)}€</td>
                      <td className="w-1/2 p-3 text-center">
                        {registration.promotions && registration.promotions.length > 0
                          ? registration.promotions.map((p) => {
                              const promo = p;
                              return (
                                <span key={promo.id}>
                                  {promo.name}
                                  <span className="ml-2 text-gray-400">
                                    ({promo.type === "percentage" ? `${promo.value} %` : `${(promo.value / 100).toFixed(2)} €`})
                                  </span>
                                </span>
                              );
                            })
                          : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Bloc Mode de paiement */}
          <Card className="gap-0 rounded-sm py-0">
            <CardHeader className="my-2 gap-0 text-center">
              <CardTitle className="text-md font-semibold">Mode de paiement</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <PaymentSection
                initialPayments={registration.paymentDetails}
                promotionDiscount={promotionDiscount}
                hideDescription={true}
                hideSubtotalColumn={true}
                subtotalValue={registration.subtotalAmount ?? 0}
                readOnly={true}
              />
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Sheet de détail enfant */}
      {selectedChild && (
        <ChildDetailSheet
          open={isChildDetailOpen}
          onOpenChange={setIsChildDetailOpen}
          child={selectedChild}
          selectedPeriodIds={(selectedChild as ChildBase & { bookedPeriods?: string[] }).bookedPeriods || []}
          initialMonth={initialMonth}
        />
      )}
    </>
  );
}
